import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table } from '@/components/table';
import type { ITableColumn } from '@/components/table/table';
import { incomingService } from '@/services/process/incoming.service';
import { Award, Send } from 'lucide-react';

interface ReleaseAllModalProps {
  open: boolean;
  onClose: () => void;
  processId: string;
  onConfirm: () => Promise<void>;
  confirming: boolean;
}

export const ReleaseAllModal: React.FC<ReleaseAllModalProps> = ({
  open,
  onClose,
  processId,
  onConfirm,
  confirming,
}) => {
  const { t } = useTranslation();
  const [affectedApplicants, setAffectedApplicants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (!open) return;

    const fetchApplicants = async () => {
      setLoading(true);
      try {
        const res = await incomingService.getApplicationsForProcess(
          processId,
          'completed',
          true,
          undefined,
          { limit: 500 },
        );
        const applicants = res?.data?.applicantProcesses || res?.applicantProcesses || [];
        setAffectedApplicants(applicants.filter((a: any) => a.hasPendingCertificate));
      } catch (err) {
        console.error('Failed to fetch applicants for release modal:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchApplicants();
  }, [open, processId]);

  const getApplicantName = (ap: any) => {
    if (ap.applicant) {
      const name = `${ap.applicant.firstName || ''} ${ap.applicant.lastName || ''}`.trim();
      return name || ap.applicant.email || 'Unknown';
    }
    return 'Unknown';
  };

  const columns: ITableColumn[] = [
    { key: 'name', label: t('processManagement.applicantName') },
    { key: 'level', label: t('processManagement.level') },
    { key: 'certificate', label: t('certificate.certificateDisplay.certificate') },
  ];

  const tableData = affectedApplicants.map((ap) => ({
    name: getApplicantName(ap),
    level: ap.processLevel || '—',
    certificate: (
      <Badge
        variant="outline"
        className="border-[#001A55]/20 bg-[#001A55]/5 text-xs text-[#001A55]"
      >
        <Award className="mr-1 h-3 w-3" />
        {t('certificate.certificateDisplay.certificate')}
      </Badge>
    ) as React.ReactElement,
  }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-[#001A55]" />
            {t('certificate.issuanceConfiguration.releaseAll')}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-[#001A55]/15 bg-[#F6F9FF] px-4 py-3 text-sm text-[#001A55]">
          {t('certificate.issuanceConfiguration.releaseAllConfirmDescription')}
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {t('processManagement.loading')}
          </p>
        ) : affectedApplicants.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            {t('certificate.issuanceConfiguration.noUnreleasedCertificates')}
          </p>
        ) : (
          <div className="py-2">
            <Table
              columns={columns}
              data={tableData}
              paginate
              showSearch
              externalSearchValue={searchValue}
              onExternalSearchChange={setSearchValue}
              isLoading={loading}
            />
          </div>
        )}

        <DialogFooter className="flex items-center justify-between pt-2">
          <p className="text-sm text-gray-500">
            {t('certificate.issuanceConfiguration.releaseAllSuccess', { count: affectedApplicants.length })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={confirming}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              {t('templateManager.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirming || affectedApplicants.length === 0}
              className="flex items-center gap-2 rounded-md bg-[#001A55] px-3 py-1.5 text-sm text-white hover:bg-[#001A55]/90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {confirming
                ? t('certificate.issuanceConfiguration.releasingAll')
                : t('certificate.issuanceConfiguration.releaseAll')}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
